import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Megaphone, Sparkles, Target, Zap, Mail, Users, BarChart3, TrendingUp, ExternalLink, Plus, Play, Pause, Send, Link2, Loader2, ChevronRight, Copy, DollarSign, Eye, MousePointer, ShoppingBag, Calendar, Radio, Flame, Layout, Clock, FlaskConical, Trash2, Globe, Share2, CreditCard, Twitter, Instagram, Linkedin } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { marketingAPI, guidanceAPI, projectsAPI, productsAPI, eventsAPI } from '../lib/api';
import { formatCurrency } from '../lib/utils';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';

function StatCard({ icon: Icon, label, value, color = 'text-zinc-400', bg = 'bg-zinc-800' }) {
  return (
    <div className="p-4 bg-zinc-900 rounded-xl border border-zinc-800" data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex items-center gap-2 mb-2">
        <div className={`w-8 h-8 rounded-lg ${bg} flex items-center justify-center`}>
          <Icon size={14} className={color} />
        </div>
        <span className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold">{label}</span>
      </div>
      <p className="text-2xl font-display font-black text-white">{value}</p>
    </div>
  );
}

export default function MarketingHubPage() {
  const { user } = useAuth();
  const [tab, setTab] = useState('dashboard');
  const [analytics, setAnalytics] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [emails, setEmails] = useState([]);
  const [automations, setAutomations] = useState([]);
  const [briefs, setBriefs] = useState([]);
  const [landingPages, setLandingPages] = useState([]);
  const [scheduled, setScheduled] = useState([]);
  const [abTests, setAbTests] = useState([]);
  const [socialPosts, setSocialPosts] = useState([]);
  const [creatorStreaks, setCreatorStreaks] = useState(null);
  const [attribution, setAttribution] = useState(null);
  const [loading, setLoading] = useState(true);

  // Campaign creation
  const [showCreate, setShowCreate] = useState(false);
  const [newCampaign, setNewCampaign] = useState({ title: '', type: 'general', goal: '', entity_type: '', entity_id: '' });
  const [creating, setCreating] = useState(false);
  const [generatingPlan, setGeneratingPlan] = useState(null);

  // Email
  const [emailForm, setEmailForm] = useState({ subject: '', body: '', audience: 'followers' });
  const [sendingEmail, setSendingEmail] = useState(false);

  // Automation
  const [autoForm, setAutoForm] = useState({ trigger: 'new_follower', subject: '', body: '', delay_hours: 0 });

  // Smart Link
  const [slug, setSlug] = useState('');
  const [savingSlug, setSavingSlug] = useState(false);

  // Promo brief
  const [briefForm, setBriefForm] = useState({ title: '', description: '', commission_rate: 5, entity_type: '', entity_id: '' });

  // Landing page
  const [lpForm, setLpForm] = useState({ title: '', slug: '', campaign_id: '' });
  const [generatingLP, setGeneratingLP] = useState(null);

  // Scheduled content
  const [schedForm, setSchedForm] = useState({ type: 'email', title: '', body: '', audience: 'followers', scheduled_for: '' });

  // A/B Test
  const [abForm, setAbForm] = useState({ subject_a: '', subject_b: '', body: '', audience: 'followers', topic: '' });
  const [runningTest, setRunningTest] = useState(null);
  const [suggestingAB, setSuggestingAB] = useState(false);

  // Social cross-posting
  const [socialForm, setSocialForm] = useState({ message: '', platforms: ['twitter', 'instagram', 'tiktok', 'linkedin'], tone: 'engaging' });
  const [generatingSocial, setGeneratingSocial] = useState(false);

  // Stripe boost
  const [boostForm, setBoostForm] = useState({ amount: 5, slot: 'homepage', duration_hours: 24 });

  useEffect(() => {
    const load = async () => {
      try {
        const [analyticsRes, campaignsRes, emailsRes, autosRes, lpRes, schedRes, abRes, socialRes, streakRes, attrRes] = await Promise.all([
          marketingAPI.analyticsOverview().catch(() => ({ data: null })),
          marketingAPI.listCampaigns().catch(() => ({ data: [] })),
          marketingAPI.emailHistory().catch(() => ({ data: [] })),
          marketingAPI.listAutomations().catch(() => ({ data: [] })),
          marketingAPI.listLandingPages().catch(() => ({ data: [] })),
          marketingAPI.listScheduled().catch(() => ({ data: [] })),
          marketingAPI.listABTests().catch(() => ({ data: [] })),
          marketingAPI.listSocialPosts().catch(() => ({ data: [] })),
          marketingAPI.creatorStreaks().catch(() => ({ data: null })),
          guidanceAPI.getAttributionOverview().catch(() => ({ data: null })),
        ]);
        setAnalytics(analyticsRes.data);
        setCampaigns(campaignsRes.data || []);
        setEmails(emailsRes.data || []);
        setAutomations(autosRes.data || []);
        setLandingPages(lpRes.data || []);
        setScheduled(schedRes.data || []);
        setAbTests(abRes.data || []);
        setSocialPosts(socialRes.data || []);
        setCreatorStreaks(streakRes.data);
        setAttribution(attrRes.data);
      } catch {}
      setLoading(false);
    };
    load();
  }, []);

  const handleCreateCampaign = async () => {
    if (!newCampaign.title) { toast.error('Campaign title required'); return; }
    setCreating(true);
    try {
      const res = await marketingAPI.createCampaign(newCampaign);
      setCampaigns([res.data, ...campaigns]);
      setShowCreate(false);
      setNewCampaign({ title: '', type: 'general', goal: '', entity_type: '', entity_id: '' });
      toast.success('Campaign created!');
    } catch { toast.error('Failed to create'); }
    setCreating(false);
  };

  const handleGeneratePlan = async (id) => {
    setGeneratingPlan(id);
    try {
      const res = await marketingAPI.generatePlan(id);
      setCampaigns(campaigns.map(c => c.id === id ? { ...c, ai_plan: res.data.plan, status: 'planned' } : c));
      toast.success('AI plan generated!');
    } catch { toast.error('Plan generation failed'); }
    setGeneratingPlan(null);
  };

  const handleActivate = async (id) => {
    try {
      await marketingAPI.activateCampaign(id);
      setCampaigns(campaigns.map(c => c.id === id ? { ...c, status: 'active' } : c));
      toast.success('Campaign activated!');
    } catch { toast.error('Activation failed'); }
  };

  const handleSendEmail = async () => {
    if (!emailForm.subject || !emailForm.body) { toast.error('Subject and body required'); return; }
    setSendingEmail(true);
    try {
      const res = await marketingAPI.sendEmail(emailForm);
      toast.success(res.data.message);
      setEmails([res.data.email, ...emails]);
      setEmailForm({ subject: '', body: '', audience: 'followers' });
    } catch (e) { toast.error(e.response?.data?.detail || 'Send failed'); }
    setSendingEmail(false);
  };

  const handleCreateAutomation = async () => {
    if (!autoForm.subject || !autoForm.body) { toast.error('Subject and body required'); return; }
    try {
      const res = await marketingAPI.createAutomation(autoForm);
      setAutomations([res.data, ...automations]);
      setAutoForm({ trigger: 'new_follower', subject: '', body: '', delay_hours: 0 });
      toast.success('Automation created!');
    } catch { toast.error('Failed'); }
  };

  const handleSaveSlug = async () => {
    if (!slug || slug.length < 3) { toast.error('Slug must be at least 3 characters'); return; }
    setSavingSlug(true);
    try {
      await marketingAPI.setupSmartLink({ slug });
      toast.success(`Smart link: /m/${slug}`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    setSavingSlug(false);
  };

  const handleCreateBrief = async () => {
    if (!briefForm.title) { toast.error('Title required'); return; }
    try {
      const res = await marketingAPI.createBrief(briefForm);
      toast.success('Promo brief created!');
      setBriefForm({ title: '', description: '', commission_rate: 5, entity_type: '', entity_id: '' });
    } catch { toast.error('Failed'); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-zinc-950"><div className="w-8 h-8 border-2 border-[#b8ff00] border-t-transparent rounded-full animate-spin" /></div>;

  const s = analytics?.summary || {};

  return (
    <div className="min-h-screen bg-zinc-950 pb-20" data-testid="marketing-hub">
      <div className="bg-gradient-to-br from-zinc-900 via-zinc-950 to-zinc-900 border-b border-zinc-800">
        <div className="max-w-6xl mx-auto px-4 py-6">
          <div className="flex items-center gap-3 mb-1">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#b8ff00] to-[#00ff88] flex items-center justify-center">
              <Megaphone size={20} className="text-black" />
            </div>
            <div>
              <h1 className="font-display font-bold text-2xl text-white">Marketing Engine</h1>
              <p className="text-xs text-zinc-500">Campaigns, analytics, email, promoters — your megaphone</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 mt-4">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-zinc-900 border border-zinc-800 p-1 mb-4">
            <TabsTrigger value="dashboard" className="data-[state=active]:bg-[#b8ff00] data-[state=active]:text-black text-xs font-bold">Dashboard</TabsTrigger>
            <TabsTrigger value="campaigns" className="data-[state=active]:bg-[#b8ff00] data-[state=active]:text-black text-xs font-bold">Campaigns</TabsTrigger>
            <TabsTrigger value="email" className="data-[state=active]:bg-[#b8ff00] data-[state=active]:text-black text-xs font-bold">Email</TabsTrigger>
            <TabsTrigger value="smartlink" className="data-[state=active]:bg-[#b8ff00] data-[state=active]:text-black text-xs font-bold">Smart Link</TabsTrigger>
            <TabsTrigger value="promoters" className="data-[state=active]:bg-[#b8ff00] data-[state=active]:text-black text-xs font-bold">Promoters</TabsTrigger>
            <TabsTrigger value="landing" className="data-[state=active]:bg-[#b8ff00] data-[state=active]:text-black text-xs font-bold">Pages</TabsTrigger>
            <TabsTrigger value="schedule" className="data-[state=active]:bg-[#b8ff00] data-[state=active]:text-black text-xs font-bold">Schedule</TabsTrigger>
            <TabsTrigger value="abtest" className="data-[state=active]:bg-[#b8ff00] data-[state=active]:text-black text-xs font-bold">A/B Test</TabsTrigger>
            <TabsTrigger value="social" className="data-[state=active]:bg-[#b8ff00] data-[state=active]:text-black text-xs font-bold">Social</TabsTrigger>
            <TabsTrigger value="streaks" className="data-[state=active]:bg-[#b8ff00] data-[state=active]:text-black text-xs font-bold">Streaks</TabsTrigger>
            <TabsTrigger value="attribution" className="data-[state=active]:bg-[#b8ff00] data-[state=active]:text-black text-xs font-bold">Attribution</TabsTrigger>
          </TabsList>

          {/* ─── DASHBOARD TAB ─── */}
          <TabsContent value="dashboard">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
              <StatCard icon={DollarSign} label="Revenue" value={formatCurrency(s.total_revenue || 0)} color="text-emerald-400" bg="bg-emerald-500/10" />
              <StatCard icon={Target} label="Funded" value={formatCurrency(s.total_funded || 0)} color="text-[#b8ff00]" bg="bg-[#b8ff00]/10" />
              <StatCard icon={Users} label="Followers" value={s.followers || 0} color="text-blue-400" bg="bg-blue-500/10" />
              <StatCard icon={Eye} label="Smart Link Views" value={s.smartlink_views || 0} color="text-violet-400" bg="bg-violet-500/10" />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
              <StatCard icon={ShoppingBag} label="Orders" value={s.total_orders || 0} color="text-amber-400" bg="bg-amber-500/10" />
              <StatCard icon={Users} label="Backers" value={s.total_backers || 0} color="text-rose-400" bg="bg-rose-500/10" />
              <StatCard icon={Mail} label="Emails Sent" value={s.total_emails_sent || 0} color="text-cyan-400" bg="bg-cyan-500/10" />
              <StatCard icon={Megaphone} label="Campaigns" value={s.active_campaigns || 0} color="text-orange-400" bg="bg-orange-500/10" />
            </div>

            {/* Follower growth chart */}
            {analytics?.weekly_growth?.length > 0 && (
              <div className="bg-zinc-900 rounded-2xl border border-zinc-800 p-5 mb-4">
                <h3 className="font-bold text-sm text-white mb-3 flex items-center gap-2"><TrendingUp size={14} className="text-[#b8ff00]" /> Weekly Follower Growth</h3>
                <div className="flex items-end gap-3 h-24">
                  {analytics.weekly_growth.map((w, i) => {
                    const maxVal = Math.max(...analytics.weekly_growth.map(x => x.new_followers), 1);
                    const height = Math.max(8, (w.new_followers / maxVal) * 100);
                    return (
                      <div key={i} className="flex-1 flex flex-col items-center gap-1">
                        <span className="text-[10px] text-zinc-500 font-mono">{w.new_followers}</span>
                        <div className="w-full rounded-t-lg bg-gradient-to-t from-[#b8ff00]/20 to-[#b8ff00]/60" style={{ height: `${height}%` }} />
                        <span className="text-[9px] text-zinc-600">{w.week}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Top projects */}
            {analytics?.projects?.length > 0 && (
              <div className="bg-zinc-900 rounded-2xl border border-zinc-800 p-5">
                <h3 className="font-bold text-sm text-white mb-3">Your Projects Performance</h3>
                <div className="space-y-2">
                  {analytics.projects.map((p, i) => (
                    <div key={i} className="flex items-center justify-between p-2 bg-zinc-800 rounded-lg">
                      <span className="text-sm text-zinc-300 font-medium">{p.title}</span>
                      <div className="flex items-center gap-3">
                        <span className="text-xs text-zinc-500">{p.backers} backers</span>
                        <span className="text-sm font-bold text-[#b8ff00]">{formatCurrency(p.funded)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </TabsContent>

          {/* ─── CAMPAIGNS TAB ─── */}
          <TabsContent value="campaigns">
            <div className="flex justify-between items-center mb-4">
              <h2 className="font-bold text-lg text-white">Your Campaigns</h2>
              <Button onClick={() => setShowCreate(!showCreate)} className="bg-[#b8ff00] text-black font-bold text-xs" data-testid="create-campaign-btn">
                <Plus size={14} className="mr-1" /> New Campaign
              </Button>
            </div>

            <AnimatePresence>
              {showCreate && (
                <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden mb-4">
                  <div className="p-5 bg-zinc-900 rounded-2xl border border-zinc-800 space-y-3">
                    <Input value={newCampaign.title} onChange={e => setNewCampaign({ ...newCampaign, title: e.target.value })} placeholder="Campaign name" className="bg-zinc-800 border-zinc-700 text-white" data-testid="campaign-title-input" />
                    <div className="flex gap-2 flex-wrap">
                      {['product_launch', 'project_funding', 'event_promo', 'general'].map(t => (
                        <button key={t} onClick={() => setNewCampaign({ ...newCampaign, type: t })} className={`px-3 py-1.5 rounded-full text-xs font-bold transition-all ${newCampaign.type === t ? 'bg-[#b8ff00] text-black' : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'}`}>
                          {t.replace(/_/g, ' ')}
                        </button>
                      ))}
                    </div>
                    <Input value={newCampaign.goal} onChange={e => setNewCampaign({ ...newCampaign, goal: e.target.value })} placeholder="Goal: e.g. 'Get 50 backers in 7 days'" className="bg-zinc-800 border-zinc-700 text-white" />
                    <Button onClick={handleCreateCampaign} disabled={creating} className="bg-[#b8ff00] text-black font-bold" data-testid="submit-campaign-btn">
                      {creating ? 'Creating...' : 'Create Campaign'}
                    </Button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            <div className="space-y-3">
              {campaigns.map(c => (
                <div key={c.id} className="p-5 bg-zinc-900 rounded-2xl border border-zinc-800" data-testid={`campaign-${c.id}`}>
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="font-bold text-white">{c.title}</h3>
                      <div className="flex items-center gap-2 mt-1">
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${c.status === 'active' ? 'bg-emerald-500/10 text-emerald-400' : c.status === 'planned' ? 'bg-amber-500/10 text-amber-400' : 'bg-zinc-700 text-zinc-400'}`}>
                          {c.status}
                        </span>
                        <span className="text-[10px] text-zinc-600">{c.type?.replace(/_/g, ' ')}</span>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      {(c.status === 'draft' || c.status === 'planned') && !c.ai_plan && (
                        <Button onClick={() => handleGeneratePlan(c.id)} disabled={generatingPlan === c.id} size="sm" className="bg-violet-600 text-white text-xs" data-testid={`gen-plan-${c.id}`}>
                          {generatingPlan === c.id ? <Loader2 size={12} className="animate-spin mr-1" /> : <Sparkles size={12} className="mr-1" />}
                          {generatingPlan === c.id ? 'Generating...' : 'AI Plan'}
                        </Button>
                      )}
                      {c.status !== 'active' && c.ai_plan && (
                        <Button onClick={() => handleActivate(c.id)} size="sm" className="bg-emerald-600 text-white text-xs">
                          <Play size={12} className="mr-1" /> Activate
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* AI Plan display */}
                  {c.ai_plan && (
                    <div className="mt-3 space-y-2">
                      <p className="text-xs text-[#b8ff00] font-semibold">{c.ai_plan.summary}</p>
                      <p className="text-[10px] text-zinc-500">Target: {c.ai_plan.target_audience}</p>
                      <div className="space-y-1.5">
                        {c.ai_plan.timeline?.map((day, i) => (
                          <div key={i} className="flex gap-3 p-2.5 bg-zinc-800 rounded-lg">
                            <div className="w-8 h-8 rounded-lg bg-zinc-700 flex items-center justify-center flex-shrink-0">
                              <span className="text-xs font-bold text-zinc-300">D{day.day}</span>
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="text-xs font-bold text-white">{day.action}</span>
                                <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-zinc-700 text-zinc-400">{day.content_type}</span>
                              </div>
                              <p className="text-[11px] text-zinc-400 mt-0.5">{day.copy}</p>
                              <p className="text-[9px] text-zinc-600 mt-0.5 italic">{day.tip}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                      {c.ai_plan.boost_strategy && (
                        <p className="text-[10px] text-amber-400 mt-2"><Zap size={10} className="inline mr-1" />{c.ai_plan.boost_strategy}</p>
                      )}
                    </div>
                  )}

                  {/* Boost section for active campaigns */}
                  {c.status === 'active' && (
                    <div className="mt-3 flex items-center gap-2 p-3 bg-zinc-800/50 rounded-xl border border-zinc-700/50">
                      <Zap size={14} className="text-amber-400 flex-shrink-0" />
                      <span className="text-[10px] text-zinc-400 flex-shrink-0">Boost:</span>
                      <select value={boostForm.duration_hours} onChange={e => setBoostForm({ ...boostForm, duration_hours: parseInt(e.target.value) })} className="bg-zinc-700 text-white text-[10px] rounded px-1.5 py-1 border-none">
                        <option value={24}>24h</option><option value={48}>48h</option><option value={72}>72h</option>
                      </select>
                      <Input type="number" value={boostForm.amount} onChange={e => setBoostForm({ ...boostForm, amount: parseInt(e.target.value) || 5 })} className="w-16 bg-zinc-700 border-zinc-600 text-white text-xs h-7 text-center" />
                      <Button onClick={async () => {
                        try {
                          const res = await marketingAPI.boostStripe(c.id, boostForm);
                          if (res.data.checkout_url) { window.open(res.data.checkout_url, '_blank'); }
                          else { toast.success(res.data.message); }
                        } catch (e) { toast.error(e.response?.data?.detail || 'Boost failed'); }
                      }} size="sm" className="bg-amber-600 text-white text-[10px] h-7 px-3">
                        <CreditCard size={10} className="mr-1" /> Pay
                      </Button>
                      <Button onClick={async () => {
                        try {
                          const res = await marketingAPI.boostCampaign(c.id, boostForm);
                          toast.success(res.data.message);
                        } catch (e) { toast.error(e.response?.data?.detail || 'Insufficient balance'); }
                      }} size="sm" variant="outline" className="text-[10px] h-7 px-3 border-zinc-600 text-zinc-400">
                        Wallet
                      </Button>
                    </div>
                  )}
                </div>
              ))}
              {campaigns.length === 0 && (
                <div className="text-center py-12 text-zinc-600">
                  <Megaphone size={32} className="mx-auto mb-3 opacity-40" />
                  <p className="text-sm">No campaigns yet. Create your first one!</p>
                </div>
              )}
            </div>
          </TabsContent>

          {/* ─── EMAIL TAB ─── */}
          <TabsContent value="email">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Send email */}
              <div className="bg-zinc-900 rounded-2xl border border-zinc-800 p-5">
                <h3 className="font-bold text-white mb-3 flex items-center gap-2"><Mail size={16} className="text-cyan-400" /> Send Email Blast</h3>
                <div className="space-y-3">
                  <div className="flex gap-2">
                    {['followers', 'backers', 'all'].map(a => (
                      <button key={a} onClick={() => setEmailForm({ ...emailForm, audience: a })} className={`px-3 py-1.5 rounded-full text-xs font-bold ${emailForm.audience === a ? 'bg-[#b8ff00] text-black' : 'bg-zinc-800 text-zinc-400'}`}>
                        {a}
                      </button>
                    ))}
                  </div>
                  <Input value={emailForm.subject} onChange={e => setEmailForm({ ...emailForm, subject: e.target.value })} placeholder="Subject line" className="bg-zinc-800 border-zinc-700 text-white" data-testid="email-subject" />
                  <Textarea value={emailForm.body} onChange={e => setEmailForm({ ...emailForm, body: e.target.value })} placeholder="Email body..." rows={5} className="bg-zinc-800 border-zinc-700 text-white text-sm" data-testid="email-body" />
                  <Button onClick={handleSendEmail} disabled={sendingEmail} className="bg-cyan-600 text-white font-bold w-full" data-testid="send-email-btn">
                    <Send size={14} className="mr-2" /> {sendingEmail ? 'Sending...' : 'Send Email'}
                  </Button>
                </div>
              </div>

              {/* Automations */}
              <div className="bg-zinc-900 rounded-2xl border border-zinc-800 p-5">
                <h3 className="font-bold text-white mb-3 flex items-center gap-2"><Zap size={16} className="text-amber-400" /> Email Automations</h3>
                <div className="space-y-3 mb-4">
                  <select value={autoForm.trigger} onChange={e => setAutoForm({ ...autoForm, trigger: e.target.value })} className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-sm text-white">
                    <option value="new_follower">When someone follows you</option>
                    <option value="new_backer">When someone backs your project</option>
                    <option value="project_milestone">When project hits a milestone</option>
                  </select>
                  <Input value={autoForm.subject} onChange={e => setAutoForm({ ...autoForm, subject: e.target.value })} placeholder="Auto-reply subject" className="bg-zinc-800 border-zinc-700 text-white" />
                  <Textarea value={autoForm.body} onChange={e => setAutoForm({ ...autoForm, body: e.target.value })} placeholder="Auto-reply body..." rows={3} className="bg-zinc-800 border-zinc-700 text-white text-sm" />
                  <Button onClick={handleCreateAutomation} className="bg-amber-600 text-white font-bold w-full text-xs">
                    <Zap size={12} className="mr-1" /> Create Automation
                  </Button>
                </div>
                {automations.map(a => (
                  <div key={a.id} className="flex items-center justify-between p-2.5 bg-zinc-800 rounded-lg mb-2">
                    <div>
                      <p className="text-xs font-bold text-white">{a.subject}</p>
                      <p className="text-[10px] text-zinc-500">{a.trigger?.replace(/_/g, ' ')} · Sent {a.sent_count}x</p>
                    </div>
                    <button onClick={async () => { const res = await marketingAPI.toggleAutomation(a.id); setAutomations(automations.map(x => x.id === a.id ? { ...x, active: res.data.active } : x)); }} className={`px-2 py-1 rounded text-[10px] font-bold ${a.active ? 'bg-emerald-500/10 text-emerald-400' : 'bg-zinc-700 text-zinc-500'}`}>
                      {a.active ? 'Active' : 'Paused'}
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Email history */}
            {emails.length > 0 && (
              <div className="mt-4 bg-zinc-900 rounded-2xl border border-zinc-800 p-5">
                <h3 className="font-bold text-sm text-white mb-3">Email History</h3>
                <div className="space-y-2">
                  {emails.map(e => (
                    <div key={e.id} className="flex items-center justify-between p-2 bg-zinc-800 rounded-lg">
                      <div>
                        <p className="text-xs font-bold text-white">{e.subject}</p>
                        <p className="text-[10px] text-zinc-500">to {e.audience} · {new Date(e.sent_at).toLocaleDateString()}</p>
                      </div>
                      <span className="text-xs font-bold text-cyan-400">{e.recipient_count} sent</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </TabsContent>

          {/* ─── SMART LINK TAB ─── */}
          <TabsContent value="smartlink">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="bg-zinc-900 rounded-2xl border border-zinc-800 p-5">
                <h3 className="font-bold text-white mb-3 flex items-center gap-2"><Link2 size={16} className="text-violet-400" /> Your Smart Link</h3>
                <p className="text-xs text-zinc-500 mb-4">One link for everything — share it everywhere</p>
                <div className="flex gap-2 mb-3">
                  <div className="flex-1 flex items-center bg-zinc-800 rounded-lg border border-zinc-700 px-3">
                    <span className="text-xs text-zinc-500 mr-1">/m/</span>
                    <input value={slug} onChange={e => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))} placeholder="your-name" className="bg-transparent text-white text-sm flex-1 outline-none py-2" data-testid="slug-input" />
                  </div>
                  <Button onClick={handleSaveSlug} disabled={savingSlug} className="bg-violet-600 text-white font-bold text-xs">
                    {savingSlug ? '...' : 'Save'}
                  </Button>
                </div>
                {slug && (
                  <div className="flex items-center gap-2 p-3 bg-zinc-800 rounded-lg">
                    <span className="text-xs text-zinc-400 flex-1 truncate">{window.location.origin}/m/{slug}</span>
                    <button onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/m/${slug}`); toast.success('Copied!'); }} className="text-zinc-500 hover:text-white">
                      <Copy size={14} />
                    </button>
                  </div>
                )}

                <div className="mt-6">
                  <h4 className="text-xs font-bold text-zinc-400 uppercase tracking-wider mb-2">Your smart link includes</h4>
                  <div className="space-y-1.5">
                    {[
                      { icon: Target, label: 'Active projects with funding progress' },
                      { icon: ShoppingBag, label: 'Products with prices' },
                      { icon: Calendar, label: 'Upcoming events' },
                      { icon: Radio, label: 'Creator rooms (live indicator)' },
                      { icon: BarChart3, label: 'Social proof stats' },
                    ].map((item, i) => (
                      <div key={i} className="flex items-center gap-2 text-xs text-zinc-400">
                        <item.icon size={12} className="text-zinc-600" />
                        <span>{item.label}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="bg-zinc-900 rounded-2xl border border-zinc-800 p-5">
                <h3 className="font-bold text-white mb-3 flex items-center gap-2"><BarChart3 size={16} className="text-emerald-400" /> Press Kit</h3>
                <p className="text-xs text-zinc-500 mb-4">Auto-generated from your MMOS activity</p>
                <div className="space-y-2">
                  {[
                    { label: 'Followers', value: s.followers || 0 },
                    { label: 'Total Funded', value: formatCurrency(s.total_funded || 0) },
                    { label: 'Total Backers', value: s.total_backers || 0 },
                    { label: 'Products', value: analytics?.products?.length || 0 },
                    { label: 'Emails Sent', value: s.total_emails_sent || 0 },
                  ].map((item, i) => (
                    <div key={i} className="flex items-center justify-between p-2 bg-zinc-800 rounded-lg">
                      <span className="text-xs text-zinc-400">{item.label}</span>
                      <span className="text-sm font-bold text-white">{item.value}</span>
                    </div>
                  ))}
                </div>
                <Link to={`/m/${slug || user?.name?.toLowerCase().replace(/\s/g, '-') || 'me'}`}>
                  <Button className="w-full mt-4 bg-emerald-600 text-white font-bold text-xs">
                    <ExternalLink size={12} className="mr-1" /> Preview Smart Link
                  </Button>
                </Link>
              </div>
            </div>
          </TabsContent>

          {/* ─── PROMOTERS TAB ─── */}
          <TabsContent value="promoters">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="bg-zinc-900 rounded-2xl border border-zinc-800 p-5">
                <h3 className="font-bold text-white mb-3 flex items-center gap-2"><Users size={16} className="text-rose-400" /> Create Promo Brief</h3>
                <p className="text-xs text-zinc-500 mb-4">Post a brief for promoters to pick up and run with</p>
                <div className="space-y-3">
                  <Input value={briefForm.title} onChange={e => setBriefForm({ ...briefForm, title: e.target.value })} placeholder="Brief title: e.g. 'Promote my new EP launch'" className="bg-zinc-800 border-zinc-700 text-white" data-testid="brief-title" />
                  <Textarea value={briefForm.description} onChange={e => setBriefForm({ ...briefForm, description: e.target.value })} placeholder="What do you want promoters to do?" rows={3} className="bg-zinc-800 border-zinc-700 text-white text-sm" />
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-zinc-500">Commission:</span>
                    <Input type="number" value={briefForm.commission_rate} onChange={e => setBriefForm({ ...briefForm, commission_rate: parseInt(e.target.value) || 5 })} className="w-20 bg-zinc-800 border-zinc-700 text-white text-center" />
                    <span className="text-xs text-zinc-500">%</span>
                  </div>
                  <Button onClick={handleCreateBrief} className="bg-rose-600 text-white font-bold w-full text-xs" data-testid="create-brief-btn">
                    <Megaphone size={12} className="mr-1" /> Post Brief
                  </Button>
                </div>
              </div>

              <div className="bg-zinc-900 rounded-2xl border border-zinc-800 p-5">
                <h3 className="font-bold text-white mb-3 flex items-center gap-2"><TrendingUp size={16} className="text-emerald-400" /> Promoter Performance</h3>
                <p className="text-xs text-zinc-500 mb-4">Track referrals and commissions</p>
                <div className="grid grid-cols-3 gap-2 mb-4">
                  <div className="text-center p-3 bg-zinc-800 rounded-lg">
                    <p className="text-lg font-bold text-white">0</p>
                    <p className="text-[9px] text-zinc-500">Clicks</p>
                  </div>
                  <div className="text-center p-3 bg-zinc-800 rounded-lg">
                    <p className="text-lg font-bold text-emerald-400">0</p>
                    <p className="text-[9px] text-zinc-500">Conversions</p>
                  </div>
                  <div className="text-center p-3 bg-zinc-800 rounded-lg">
                    <p className="text-lg font-bold text-[#b8ff00]">$0</p>
                    <p className="text-[9px] text-zinc-500">Earned</p>
                  </div>
                </div>
                <p className="text-[10px] text-zinc-600 text-center">Promoter stats will populate as your briefs get picked up</p>
              </div>
            </div>
          </TabsContent>

          {/* ─── LANDING PAGES TAB ─── */}
          <TabsContent value="landing">
            <div className="space-y-4">
              <div className="bg-zinc-900 rounded-2xl border border-zinc-800 p-5">
                <h3 className="font-bold text-white mb-3 flex items-center gap-2"><Layout size={16} className="text-violet-400" /> Create Landing Page</h3>
                <div className="space-y-3">
                  <Input value={lpForm.title} onChange={e => setLpForm({ ...lpForm, title: e.target.value })} placeholder="Page title (e.g. 'EP Launch')" className="bg-zinc-800 border-zinc-700 text-white" data-testid="lp-title" />
                  <div className="flex gap-2">
                    <div className="flex-1 flex items-center bg-zinc-800 rounded-lg border border-zinc-700 px-3">
                      <span className="text-xs text-zinc-500 mr-1">/lp/</span>
                      <input value={lpForm.slug} onChange={e => setLpForm({ ...lpForm, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })} placeholder="my-launch" className="bg-transparent text-white text-sm flex-1 outline-none py-2" />
                    </div>
                    <Button onClick={async () => {
                      if (!lpForm.title) { toast.error('Title required'); return; }
                      try {
                        const res = await marketingAPI.createLandingPage({ ...lpForm, slug: lpForm.slug || lpForm.title.toLowerCase().replace(/\s+/g, '-') });
                        setLandingPages([res.data, ...landingPages]);
                        setLpForm({ title: '', slug: '', campaign_id: '' });
                        toast.success('Page created!');
                      } catch { toast.error('Failed'); }
                    }} className="bg-violet-600 text-white font-bold text-xs" data-testid="create-lp-btn">
                      <Plus size={12} className="mr-1" /> Create
                    </Button>
                  </div>
                </div>
              </div>

              {landingPages.map(lp => (
                <div key={lp.id} className="p-5 bg-zinc-900 rounded-2xl border border-zinc-800" data-testid={`lp-${lp.id}`}>
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="font-bold text-white">{lp.title || 'Untitled'}</h3>
                      <p className="text-[10px] text-zinc-500 mt-0.5">/lp/{lp.slug} · {lp.stats?.views || 0} views · {lp.stats?.cta_clicks || 0} clicks</p>
                    </div>
                    <div className="flex gap-2">
                      {!lp.headline && (
                        <Button onClick={async () => {
                          setGeneratingLP(lp.id);
                          try {
                            const res = await marketingAPI.generateLandingPage(lp.id);
                            setLandingPages(landingPages.map(p => p.id === lp.id ? { ...p, ...res.data.generated } : p));
                            toast.success('Content generated!');
                          } catch { toast.error('Failed'); }
                          setGeneratingLP(null);
                        }} disabled={generatingLP === lp.id} size="sm" className="bg-violet-600 text-white text-xs">
                          {generatingLP === lp.id ? <Loader2 size={12} className="animate-spin mr-1" /> : <Sparkles size={12} className="mr-1" />}
                          AI Generate
                        </Button>
                      )}
                      {!lp.published && (
                        <Button onClick={async () => {
                          await marketingAPI.publishLandingPage(lp.id);
                          setLandingPages(landingPages.map(p => p.id === lp.id ? { ...p, published: true } : p));
                          toast.success('Published!');
                        }} size="sm" className="bg-emerald-600 text-white text-xs">
                          <Globe size={12} className="mr-1" /> Publish
                        </Button>
                      )}
                      {lp.published && (
                        <a href={`${window.location.origin}/lp/${lp.slug}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 px-2 py-1 bg-zinc-800 rounded text-[10px] text-zinc-400 hover:text-white">
                          <ExternalLink size={10} /> View Live
                        </a>
                      )}
                    </div>
                  </div>
                  {lp.headline && (
                    <div className="p-3 bg-zinc-800 rounded-xl">
                      <p className="text-sm font-bold text-white">{lp.headline}</p>
                      <p className="text-xs text-zinc-400 mt-1">{lp.subheadline}</p>
                      {lp.sections?.map((s, i) => (
                        <div key={i} className="mt-2 p-2 bg-zinc-700/50 rounded-lg">
                          <p className="text-xs font-bold text-zinc-300">{s.title}</p>
                          <p className="text-[10px] text-zinc-500">{s.body}</p>
                        </div>
                      ))}
                      <div className="mt-2 inline-block px-3 py-1 bg-[#b8ff00] text-black text-xs font-bold rounded-full">{lp.cta_text || 'Get Started'}</div>
                    </div>
                  )}
                  <span className={`inline-block mt-2 px-2 py-0.5 rounded-full text-[9px] font-bold ${lp.published ? 'bg-emerald-500/10 text-emerald-400' : 'bg-zinc-700 text-zinc-400'}`}>
                    {lp.published ? 'Live' : 'Draft'}
                  </span>
                </div>
              ))}
              {landingPages.length === 0 && (
                <div className="text-center py-8 text-zinc-600"><Layout size={28} className="mx-auto mb-2 opacity-40" /><p className="text-sm">No landing pages yet</p></div>
              )}
            </div>
          </TabsContent>

          {/* ─── SCHEDULE TAB ─── */}
          <TabsContent value="schedule">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="bg-zinc-900 rounded-2xl border border-zinc-800 p-5">
                <h3 className="font-bold text-white mb-3 flex items-center gap-2"><Clock size={16} className="text-amber-400" /> Schedule Content</h3>
                <div className="space-y-3">
                  <div className="flex gap-2">
                    {['email', 'post', 'boost'].map(t => (
                      <button key={t} onClick={() => setSchedForm({ ...schedForm, type: t })} className={`px-3 py-1.5 rounded-full text-xs font-bold ${schedForm.type === t ? 'bg-[#b8ff00] text-black' : 'bg-zinc-800 text-zinc-400'}`}>{t}</button>
                    ))}
                  </div>
                  <Input value={schedForm.title} onChange={e => setSchedForm({ ...schedForm, title: e.target.value })} placeholder="Title / Subject line" className="bg-zinc-800 border-zinc-700 text-white" data-testid="sched-title" />
                  <Textarea value={schedForm.body} onChange={e => setSchedForm({ ...schedForm, body: e.target.value })} placeholder="Content body..." rows={3} className="bg-zinc-800 border-zinc-700 text-white text-sm" />
                  <div className="flex gap-2">
                    {['followers', 'backers', 'all'].map(a => (
                      <button key={a} onClick={() => setSchedForm({ ...schedForm, audience: a })} className={`px-2 py-1 rounded text-[10px] font-bold ${schedForm.audience === a ? 'bg-[#b8ff00] text-black' : 'bg-zinc-800 text-zinc-400'}`}>{a}</button>
                    ))}
                  </div>
                  <Input type="datetime-local" value={schedForm.scheduled_for} onChange={e => setSchedForm({ ...schedForm, scheduled_for: e.target.value })} className="bg-zinc-800 border-zinc-700 text-white" data-testid="sched-datetime" />
                  <Button onClick={async () => {
                    if (!schedForm.title || !schedForm.scheduled_for) { toast.error('Title and date required'); return; }
                    try {
                      const res = await marketingAPI.scheduleContent({ ...schedForm, scheduled_for: new Date(schedForm.scheduled_for).toISOString() });
                      setScheduled([res.data, ...scheduled]);
                      setSchedForm({ type: 'email', title: '', body: '', audience: 'followers', scheduled_for: '' });
                      toast.success('Scheduled!');
                    } catch { toast.error('Failed'); }
                  }} className="bg-amber-600 text-white font-bold w-full text-xs" data-testid="schedule-btn">
                    <Clock size={12} className="mr-1" /> Schedule
                  </Button>
                </div>
              </div>

              <div className="bg-zinc-900 rounded-2xl border border-zinc-800 p-5">
                <h3 className="font-bold text-white mb-3">Upcoming Content</h3>
                {scheduled.filter(s => s.status === 'scheduled').length === 0 && (
                  <p className="text-xs text-zinc-600 text-center py-6">Nothing scheduled yet</p>
                )}
                {scheduled.filter(s => s.status === 'scheduled').map(s => (
                  <div key={s.id} className="flex items-center justify-between p-3 bg-zinc-800 rounded-lg mb-2">
                    <div>
                      <p className="text-xs font-bold text-white">{s.title}</p>
                      <p className="text-[10px] text-zinc-500">{s.type} · {s.audience} · {new Date(s.scheduled_for).toLocaleString()}</p>
                    </div>
                    <button onClick={async () => {
                      await marketingAPI.cancelScheduled(s.id);
                      setScheduled(scheduled.filter(x => x.id !== s.id));
                      toast.success('Cancelled');
                    }} className="text-zinc-500 hover:text-red-400"><Trash2 size={14} /></button>
                  </div>
                ))}
                {scheduled.filter(s => s.status === 'published').length > 0 && (
                  <>
                    <p className="text-[10px] text-zinc-600 uppercase tracking-wider font-bold mt-4 mb-2">Published</p>
                    {scheduled.filter(s => s.status === 'published').map(s => (
                      <div key={s.id} className="flex items-center justify-between p-2 bg-zinc-800/50 rounded-lg mb-1">
                        <p className="text-[10px] text-zinc-400">{s.title}</p>
                        <span className="text-[9px] text-emerald-400">sent</span>
                      </div>
                    ))}
                  </>
                )}
              </div>
            </div>
          </TabsContent>

          {/* ─── A/B TEST TAB ─── */}
          <TabsContent value="abtest">
            <div className="space-y-4">
              <div className="bg-zinc-900 rounded-2xl border border-zinc-800 p-5">
                <h3 className="font-bold text-white mb-3 flex items-center gap-2"><FlaskConical size={16} className="text-cyan-400" /> A/B Test Email Subjects</h3>
                <p className="text-xs text-zinc-500 mb-4">Test two subject lines. We split your audience and show you which wins.</p>
                <div className="space-y-3">
                  <div className="flex gap-2">
                    <Input value={abForm.topic} onChange={e => setAbForm({ ...abForm, topic: e.target.value })} placeholder="Topic (e.g. 'EP launch announcement')" className="flex-1 bg-zinc-800 border-zinc-700 text-white" data-testid="ab-topic" />
                    <Button onClick={async () => {
                      if (!abForm.topic) { toast.error('Topic required'); return; }
                      setSuggestingAB(true);
                      try {
                        const res = await marketingAPI.aiSuggestSubjects('new', { topic: abForm.topic });
                        setAbForm({ ...abForm, subject_a: res.data.subject_a, subject_b: res.data.subject_b });
                        toast.success('AI suggestions ready!');
                      } catch { toast.error('Failed'); }
                      setSuggestingAB(false);
                    }} disabled={suggestingAB} className="bg-violet-600 text-white text-xs font-bold">
                      {suggestingAB ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
                    </Button>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <p className="text-[10px] text-zinc-500 mb-1 font-bold">Variant A</p>
                      <Input value={abForm.subject_a} onChange={e => setAbForm({ ...abForm, subject_a: e.target.value })} placeholder="Subject A (direct)" className="bg-zinc-800 border-zinc-700 text-white" data-testid="ab-subject-a" />
                    </div>
                    <div>
                      <p className="text-[10px] text-zinc-500 mb-1 font-bold">Variant B</p>
                      <Input value={abForm.subject_b} onChange={e => setAbForm({ ...abForm, subject_b: e.target.value })} placeholder="Subject B (curiosity)" className="bg-zinc-800 border-zinc-700 text-white" data-testid="ab-subject-b" />
                    </div>
                  </div>
                  <Textarea value={abForm.body} onChange={e => setAbForm({ ...abForm, body: e.target.value })} placeholder="Email body (same for both variants)" rows={3} className="bg-zinc-800 border-zinc-700 text-white text-sm" />
                  <Button onClick={async () => {
                    if (!abForm.subject_a || !abForm.subject_b) { toast.error('Both subjects required'); return; }
                    try {
                      const res = await marketingAPI.createABTest(abForm);
                      setAbTests([res.data, ...abTests]);
                      setAbForm({ subject_a: '', subject_b: '', body: '', audience: 'followers', topic: '' });
                      toast.success('A/B test created!');
                    } catch { toast.error('Failed'); }
                  }} className="bg-cyan-600 text-white font-bold w-full text-xs" data-testid="create-ab-btn">
                    <FlaskConical size={12} className="mr-1" /> Create A/B Test
                  </Button>
                </div>
              </div>

              {abTests.map(test => (
                <div key={test.id} className="p-5 bg-zinc-900 rounded-2xl border border-zinc-800" data-testid={`ab-${test.id}`}>
                  <div className="flex items-center justify-between mb-3">
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${test.status === 'completed' ? 'bg-emerald-500/10 text-emerald-400' : test.status === 'testing' ? 'bg-amber-500/10 text-amber-400' : 'bg-zinc-700 text-zinc-400'}`}>
                      {test.status}
                    </span>
                    {test.status === 'draft' && (
                      <Button onClick={async () => {
                        setRunningTest(test.id);
                        try {
                          const res = await marketingAPI.runABTest(test.id);
                          setAbTests(abTests.map(t => t.id === test.id ? { ...t, ...res.data, status: 'completed', winner: res.data.winner } : t));
                          toast.success(`Winner: Variant ${res.data.winner}!`);
                        } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
                        setRunningTest(null);
                      }} disabled={runningTest === test.id} size="sm" className="bg-cyan-600 text-white text-xs">
                        {runningTest === test.id ? <Loader2 size={12} className="animate-spin mr-1" /> : <Play size={12} className="mr-1" />}
                        Run Test
                      </Button>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className={`p-3 rounded-xl border ${test.winner === 'A' ? 'border-emerald-500/30 bg-emerald-500/5' : 'border-zinc-700 bg-zinc-800'}`}>
                      <div className="flex items-center gap-1 mb-1">
                        <span className="text-[10px] font-bold text-zinc-500">Variant A</span>
                        {test.winner === 'A' && <span className="text-[9px] font-bold text-emerald-400 bg-emerald-500/10 px-1.5 rounded">WINNER</span>}
                      </div>
                      <p className="text-xs font-bold text-white mb-1">{test.variant_a?.subject}</p>
                      {test.variant_a?.sent > 0 && (
                        <div className="text-[10px] text-zinc-500">
                          <span>{test.variant_a.sent} sent</span> · <span>{test.variant_a.opens} opens</span> · <span className="text-white font-bold">{test.variant_a.sent > 0 ? Math.round((test.variant_a.opens / test.variant_a.sent) * 100) : 0}% open rate</span>
                        </div>
                      )}
                    </div>
                    <div className={`p-3 rounded-xl border ${test.winner === 'B' ? 'border-emerald-500/30 bg-emerald-500/5' : 'border-zinc-700 bg-zinc-800'}`}>
                      <div className="flex items-center gap-1 mb-1">
                        <span className="text-[10px] font-bold text-zinc-500">Variant B</span>
                        {test.winner === 'B' && <span className="text-[9px] font-bold text-emerald-400 bg-emerald-500/10 px-1.5 rounded">WINNER</span>}
                      </div>
                      <p className="text-xs font-bold text-white mb-1">{test.variant_b?.subject}</p>
                      {test.variant_b?.sent > 0 && (
                        <div className="text-[10px] text-zinc-500">
                          <span>{test.variant_b.sent} sent</span> · <span>{test.variant_b.opens} opens</span> · <span className="text-white font-bold">{test.variant_b.sent > 0 ? Math.round((test.variant_b.opens / test.variant_b.sent) * 100) : 0}% open rate</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
              {abTests.length === 0 && (
                <div className="text-center py-8 text-zinc-600"><FlaskConical size={28} className="mx-auto mb-2 opacity-40" /><p className="text-sm">No A/B tests yet</p></div>
              )}
            </div>
          </TabsContent>

          {/* ─── SOCIAL CROSS-POSTING TAB ─── */}
          <TabsContent value="social">
            <div className="space-y-4">
              <div className="bg-zinc-900 rounded-2xl border border-zinc-800 p-5">
                <h3 className="font-bold text-white mb-1 flex items-center gap-2"><Share2 size={16} className="text-cyan-400" /> Cross-Post Generator</h3>
                <p className="text-xs text-zinc-500 mb-4">Write once. AI optimizes for every platform.</p>
                <div className="space-y-3">
                  <Textarea value={socialForm.message} onChange={e => setSocialForm({ ...socialForm, message: e.target.value })} placeholder="What do you want to tell the world? (e.g. 'My new EP drops Friday')" rows={3} className="bg-zinc-800 border-zinc-700 text-white text-sm" data-testid="social-message" />
                  <div className="flex gap-2 flex-wrap">
                    {['twitter', 'instagram', 'tiktok', 'linkedin'].map(p => {
                      const active = socialForm.platforms.includes(p);
                      return (
                        <button key={p} onClick={() => setSocialForm({ ...socialForm, platforms: active ? socialForm.platforms.filter(x => x !== p) : [...socialForm.platforms, p] })}
                          className={`px-3 py-1.5 rounded-full text-xs font-bold capitalize ${active ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/30' : 'bg-zinc-800 text-zinc-500'}`}
                          data-testid={`platform-${p}`}>
                          {p}
                        </button>
                      );
                    })}
                  </div>
                  <div className="flex gap-2">
                    {['engaging', 'professional', 'casual', 'hype', 'mysterious'].map(t => (
                      <button key={t} onClick={() => setSocialForm({ ...socialForm, tone: t })}
                        className={`px-2 py-1 rounded text-[10px] font-bold capitalize ${socialForm.tone === t ? 'bg-[#b8ff00] text-black' : 'bg-zinc-800 text-zinc-500'}`}>
                        {t}
                      </button>
                    ))}
                  </div>
                  <Button onClick={async () => {
                    if (!socialForm.message) { toast.error('Message required'); return; }
                    setGeneratingSocial(true);
                    try {
                      const res = await marketingAPI.generateSocialPosts(socialForm);
                      setSocialPosts([res.data.record, ...socialPosts]);
                      setSocialForm({ ...socialForm, message: '' });
                      toast.success('Posts generated for ' + Object.keys(res.data.posts).length + ' platforms!');
                    } catch { toast.error('Generation failed'); }
                    setGeneratingSocial(false);
                  }} disabled={generatingSocial} className="bg-cyan-600 text-white font-bold w-full" data-testid="generate-social-btn">
                    {generatingSocial ? <><Loader2 size={14} className="animate-spin mr-2" /> Generating...</> : <><Sparkles size={14} className="mr-2" /> Generate Posts</>}
                  </Button>
                </div>
              </div>

              {/* Generated posts */}
              {socialPosts.map(sp => (
                <div key={sp.id} className="p-5 bg-zinc-900 rounded-2xl border border-zinc-800" data-testid={`social-${sp.id}`}>
                  <p className="text-xs text-zinc-500 mb-3">{sp.message}</p>
                  <div className="space-y-2">
                    {Object.entries(sp.posts || {}).map(([platform, text]) => (
                      <div key={platform} className="p-3 bg-zinc-800 rounded-xl">
                        <div className="flex items-center justify-between mb-1.5">
                          <span className="text-[10px] font-bold uppercase tracking-wider text-zinc-400 capitalize">{platform}</span>
                          <button onClick={() => { navigator.clipboard.writeText(text); marketingAPI.trackCopy(sp.id, { platform }).catch(() => {}); toast.success(`${platform} post copied!`); }}
                            className="flex items-center gap-1 text-[10px] text-zinc-500 hover:text-[#b8ff00] transition-colors" data-testid={`copy-${platform}`}>
                            <Copy size={10} /> Copy
                          </button>
                        </div>
                        <p className="text-xs text-zinc-300 whitespace-pre-wrap leading-relaxed">{text}</p>
                      </div>
                    ))}
                  </div>
                  <p className="text-[9px] text-zinc-600 mt-2">{new Date(sp.created_at).toLocaleString()}</p>
                </div>
              ))}
              {socialPosts.length === 0 && !generatingSocial && (
                <div className="text-center py-8 text-zinc-600"><Share2 size={28} className="mx-auto mb-2 opacity-40" /><p className="text-sm">No social posts yet</p></div>
              )}
            </div>
          </TabsContent>

          {/* ─── CREATOR STREAKS TAB ─── */}
          <TabsContent value="streaks">
            {creatorStreaks ? (
              <div className="space-y-4">
                {/* Streak Score */}
                <div className="bg-zinc-900 rounded-2xl border border-zinc-800 p-6 text-center">
                  <p className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold mb-2">Creator Momentum</p>
                  <p className="text-5xl font-display font-black text-white mb-1">{creatorStreaks.streak_score}</p>
                  <p className="text-sm text-[#b8ff00] font-semibold">{creatorStreaks.message}</p>
                </div>

                {/* Weekly Stats */}
                <div className="grid grid-cols-3 md:grid-cols-5 gap-2">
                  {[
                    { label: 'Updates', value: creatorStreaks.stats?.week_updates || 0, icon: '✏️' },
                    { label: 'Live Sessions', value: creatorStreaks.stats?.week_live_sessions || 0, icon: '📡' },
                    { label: 'Tips', value: creatorStreaks.stats?.week_tips_received || 0, icon: '💰' },
                    { label: 'Campaigns', value: creatorStreaks.stats?.week_campaigns || 0, icon: '📣' },
                    { label: 'Social Posts', value: creatorStreaks.stats?.week_social_posts || 0, icon: '📱' },
                  ].map(s => (
                    <div key={s.label} className="p-3 bg-zinc-900 rounded-xl border border-zinc-800 text-center">
                      <p className="text-lg mb-0.5">{s.icon}</p>
                      <p className="text-xl font-bold text-white">{s.value}</p>
                      <p className="text-[9px] text-zinc-500 uppercase font-bold">{s.label}</p>
                    </div>
                  ))}
                </div>

                {/* Active Streaks */}
                {creatorStreaks.streaks?.length > 0 && (
                  <div className="bg-zinc-900 rounded-2xl border border-zinc-800 p-5">
                    <p className="text-[10px] font-bold text-zinc-500 uppercase tracking-wider mb-3">Active Streaks</p>
                    <div className="space-y-2">
                      {creatorStreaks.streaks.map((streak, i) => (
                        <div key={i} className={`flex items-center gap-3 p-3 rounded-xl border ${streak.level === 'fire' ? 'border-amber-500/30 bg-amber-500/5' : 'border-zinc-700 bg-zinc-800'}`}>
                          <span className="text-lg">{streak.level === 'fire' ? '🔥' : '⚡'}</span>
                          <span className="text-sm font-bold text-white flex-1">{streak.label}</span>
                          {streak.level === 'fire' && <span className="text-[9px] font-bold text-amber-400 uppercase tracking-wider">on fire</span>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Badges */}
                {creatorStreaks.badges?.length > 0 && (
                  <div className="bg-zinc-900 rounded-2xl border border-zinc-800 p-5">
                    <p className="text-[10px] font-bold text-zinc-500 uppercase tracking-wider mb-3">Creator Badges</p>
                    <div className="flex flex-wrap gap-2">
                      {creatorStreaks.badges.map((badge, i) => (
                        <span key={i} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-zinc-800 border border-zinc-700 rounded-full text-xs font-bold text-white">
                          <span>🏅</span> {badge.label}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* All-time stats */}
                <div className="bg-zinc-900 rounded-2xl border border-zinc-800 p-5">
                  <p className="text-[10px] font-bold text-zinc-500 uppercase tracking-wider mb-3">All-Time Stats</p>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="text-center"><p className="text-2xl font-bold text-white">{creatorStreaks.stats?.total_projects || 0}</p><p className="text-[9px] text-zinc-500">Projects</p></div>
                    <div className="text-center"><p className="text-2xl font-bold text-[#b8ff00]">${creatorStreaks.stats?.total_funded || 0}</p><p className="text-[9px] text-zinc-500">Funded</p></div>
                    <div className="text-center"><p className="text-2xl font-bold text-white">{creatorStreaks.stats?.total_followers || 0}</p><p className="text-[9px] text-zinc-500">Followers</p></div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-12 text-zinc-600"><Flame size={32} className="mx-auto mb-3 opacity-40" /><p className="text-sm">Loading streaks...</p></div>
            )}
          </TabsContent>

          {/* ─── ATTRIBUTION TAB ─── */}
          <TabsContent value="attribution">
            {attribution ? (
              <div className="space-y-4">
                {/* Overview cards */}
                <div className="grid grid-cols-3 gap-3">
                  <div className="p-5 bg-zinc-900 rounded-2xl border border-zinc-800 text-center">
                    <p className="text-3xl font-display font-black text-white">{attribution.total_touches || 0}</p>
                    <p className="text-[10px] text-zinc-500 uppercase font-bold mt-1">Total Touches</p>
                  </div>
                  <div className="p-5 bg-zinc-900 rounded-2xl border border-zinc-800 text-center">
                    <p className="text-3xl font-display font-black text-emerald-400">{attribution.total_conversions || 0}</p>
                    <p className="text-[10px] text-zinc-500 uppercase font-bold mt-1">Conversions</p>
                  </div>
                  <div className="p-5 bg-zinc-900 rounded-2xl border border-zinc-800 text-center">
                    <p className="text-3xl font-display font-black text-[#b8ff00]">{attribution.conversion_rate || 0}%</p>
                    <p className="text-[10px] text-zinc-500 uppercase font-bold mt-1">Conversion Rate</p>
                  </div>
                </div>

                {/* Source breakdown */}
                {Object.keys(attribution.sources || {}).length > 0 ? (
                  <div className="bg-zinc-900 rounded-2xl border border-zinc-800 p-5">
                    <h3 className="font-bold text-sm text-white mb-4 flex items-center gap-2"><BarChart3 size={14} className="text-[#b8ff00]" /> Traffic Sources</h3>
                    <div className="space-y-2">
                      {Object.entries(attribution.sources).sort((a, b) => b[1].touches - a[1].touches).map(([source, data]) => {
                        const colors = { direct: 'bg-zinc-500', smartlink: 'bg-violet-500', campaign: 'bg-amber-500', social: 'bg-cyan-500', email: 'bg-emerald-500', promoter: 'bg-rose-500', search: 'bg-blue-500', bridge: 'bg-[#b8ff00]' };
                        return (
                          <div key={source} className="flex items-center gap-3">
                            <div className={`w-3 h-3 rounded-full ${colors[source] || 'bg-zinc-600'}`} />
                            <span className="text-xs font-bold text-white capitalize flex-1">{source}</span>
                            <span className="text-xs text-zinc-400">{data.touches} touches</span>
                            <div className="w-24 h-2 bg-zinc-800 rounded-full overflow-hidden">
                              <div className={`h-full rounded-full ${colors[source] || 'bg-zinc-500'}`} style={{ width: `${data.pct}%` }} />
                            </div>
                            <span className="text-xs font-bold text-zinc-300 w-10 text-right">{data.pct}%</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ) : (
                  <div className="bg-zinc-900 rounded-2xl border border-zinc-800 p-5">
                    <h3 className="font-bold text-sm text-white mb-3 flex items-center gap-2"><BarChart3 size={14} className="text-[#b8ff00]" /> Traffic Sources</h3>
                    <p className="text-xs text-zinc-500 text-center py-4">No attribution data yet. As visitors interact with your content through different channels (smart links, campaigns, email, social, promoters), their journey will appear here.</p>
                  </div>
                )}

                {/* Top campaigns */}
                {attribution.top_campaigns?.length > 0 && (
                  <div className="bg-zinc-900 rounded-2xl border border-zinc-800 p-5">
                    <h3 className="font-bold text-sm text-white mb-3">Top Campaigns by Touches</h3>
                    {attribution.top_campaigns.map((c, i) => (
                      <div key={i} className="flex items-center justify-between p-2 bg-zinc-800 rounded-lg mb-1.5">
                        <span className="text-xs text-zinc-300 font-mono truncate flex-1">{c.campaign_id.slice(0, 8)}...</span>
                        <span className="text-sm font-bold text-amber-400">{c.touches} touches</span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Top promoters */}
                {attribution.top_promoters?.length > 0 && (
                  <div className="bg-zinc-900 rounded-2xl border border-zinc-800 p-5">
                    <h3 className="font-bold text-sm text-white mb-3">Top Promoters</h3>
                    {attribution.top_promoters.map((p, i) => (
                      <div key={i} className="flex items-center justify-between p-2 bg-zinc-800 rounded-lg mb-1.5">
                        <span className="text-xs text-zinc-300 font-mono">{p.referral_code}</span>
                        <span className="text-sm font-bold text-rose-400">{p.touches} touches</span>
                      </div>
                    ))}
                  </div>
                )}

                {/* How attribution works */}
                <div className="bg-zinc-900 rounded-2xl border border-zinc-800 p-5">
                  <h3 className="font-bold text-sm text-white mb-3">How Attribution Works</h3>
                  <div className="grid grid-cols-2 gap-3">
                    {[
                      { model: 'First Touch', desc: '100% credit to the first interaction that brought the user', color: 'text-emerald-400' },
                      { model: 'Last Touch', desc: '100% credit to the last interaction before conversion', color: 'text-blue-400' },
                      { model: 'Linear', desc: 'Equal credit distributed across all touchpoints', color: 'text-amber-400' },
                      { model: 'Time Decay', desc: 'More recent touchpoints get more credit', color: 'text-violet-400' },
                    ].map(m => (
                      <div key={m.model} className="p-3 bg-zinc-800 rounded-xl">
                        <p className={`text-xs font-bold ${m.color}`}>{m.model}</p>
                        <p className="text-[10px] text-zinc-500 mt-1">{m.desc}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-12 text-zinc-600"><BarChart3 size={32} className="mx-auto mb-3 opacity-40" /><p className="text-sm">Loading attribution data...</p></div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
